import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import WhyChooseUs from './components/WhyChooseUs';
import Expertise from './components/Expertise';
import Products from './components/Products';
import Careers from './components/Careers';
import InternshipApply from './components/Intern/InternshipApply';
import InternshipRolePage from './components/Intern/InternshipRolePage';
import InternForgot from './components/Intern/InternForgot';
import InternLogin from './components/Intern/InternLogin';
import InternPortal from './components/Intern/InternPortal';
import CertificateSearch from './components/Intern/CertificateSearch';
import LetterView from './components/Intern/LetterView';
import Clients from './components/Clients';
import Contact from './components/Contact';
import Footer from './components/Footer';
import About from './components/About';
import ServicesPage from './components/ServicesPage';
import ContactPage from './components/ContactPage';
import ServiceDetail from './components/ServiceDetail';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';
import AdminGate from './components/Admin/AdminGate';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function HashScroll() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const timer = setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 80);
    return () => clearTimeout(timer);
  }, [pathname, hash]);

  return null;
}

function App() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.theme = 'dark';
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.theme = 'light';
    }
  }, [isDarkMode]);

  useEffect(() => {
    const cards = document.querySelectorAll('.card-3d');
    cards.forEach((card, i) => {
      gsap.from(card, {
        scrollTrigger: {
          trigger: card,
          start: 'top 90%',
        },
        y: 50,
        opacity: 0,
        duration: 0.6,
        delay: i * 0.1,
        ease: 'power2.out'
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newMode = !prev;
      if (newMode) {
        document.documentElement.classList.add('dark');
        localStorage.theme = 'dark';
      } else {
        document.documentElement.classList.remove('dark');
        localStorage.theme = 'light';
      }
      return newMode;
    });
  };

  return (
    <Router>
      <AppShell isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
    </Router>
  );
}

function AppShell({ isDarkMode, toggleTheme }) {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const isInternApp = location.pathname.startsWith('/intern/');
  const isLetterPage = location.pathname.startsWith('/verify/');
  const hidePublicChrome = isAdmin || isInternApp || isLetterPage;

  return (
    <>
      <HashScroll />
      <div className="min-h-screen">
        {!hidePublicChrome && <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />}
        <main>
          <Routes>
            <Route path="/" element={
              <>
                <Hero />
                <WhyChooseUs />
                <Expertise />
                <Products />
                <Clients />
                <Contact />
              </>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/internship" element={<Careers />} />
            <Route path="/internship/apply/:slug" element={<InternshipApply />} />
            <Route path="/internship/:slug" element={<InternshipRolePage />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/intern/login" element={<InternLogin />} />
            <Route path="/intern/forgot" element={<InternForgot />} />
            <Route path="/intern/portal/*" element={<InternPortal />} />
            <Route path="/certificates" element={<CertificateSearch />} />
            <Route path="/verify/:code" element={<LetterView />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/admin" element={<AdminGate />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<Navigate to="/admin/interns" replace />} />
            <Route path="/admin/interns/certificate" element={<AdminDashboard />} />
            <Route path="/admin/interns/:internId" element={<AdminDashboard />} />
            <Route path="/admin/:page" element={<AdminDashboard />} />
          </Routes>
        </main>
        {!hidePublicChrome && <Footer />}
      </div>
    </>
  );
}

export default App;
